interface Ticket {
    id: number;
    created_at: string;
    target_date: string;
    order_number: string;
    reason: string;
    priority: string;
    responsible: string;
    created_by_user?: { username: string };
    updated_at: string;
    status: string;
    notified_at: string | null;
}

interface Props {
    tickets: Ticket[];
    onSelect: (id: number) => void;
    emptyMessage?: string;
    showDays?: boolean;
    showNotified?: boolean;
    onMarkNotified?: (id: number) => void;
}

export default function TicketList({ tickets, onSelect, emptyMessage = 'No hay tickets', showDays = false, showNotified = false, onMarkNotified }: Props) {
    if (tickets.length === 0) {
        return <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>{emptyMessage}</div>;
    }

    const calculateDays = (date: string) => {
        const diff = new Date().getTime() - new Date(date).getTime();
        return Math.floor(diff / (1000 * 3600 * 24));
    };

    const calculateDaysToTarget = (targetDate: string) => {
        if (!targetDate) return null;
        const diff = new Date(targetDate).getTime() - new Date().getTime();
        return Math.ceil(diff / (1000 * 3600 * 24));
    };

    const getPriorityColor = (priority: string) => {
        switch (priority) {
            case 'Urgent': return '#dc3545';
            case 'High': return '#ffc107';
            case 'Medium': return '#17a2b8';
            case 'Low': return '#28a745';
            default: return 'var(--text-primary)';
        }
    };

    return (
        <div className="table-container">
            <table className="table">
                <thead>
                    <tr>
                        <th>ID</th>
                        <th>Fecha</th>
                        <th>Objetivo</th>
                        <th>Prioridad</th>
                        <th>Pedido</th>
                        <th>Motivo</th>
                        <th>Responsable</th>
                        {showDays && (
                           <>
                             <th>Días Abierto</th>
                             <th>Días Restantes</th>
                           </>
                        )}
                        {showNotified && <th>Notificado</th>}
                        <th>Creado por</th>
                        <th>Acción</th>
                    </tr>
                </thead>
                <tbody>
                    {tickets.map(ticket => {
                        const daysRemaining = calculateDaysToTarget(ticket.target_date);
                        return (
                        <tr key={ticket.id}>
                            <td>#{ticket.id}</td>
                            <td>{new Date(ticket.created_at).toLocaleDateString()}</td>
                            <td>
                                {ticket.target_date ? new Date(ticket.target_date).toLocaleDateString() : '-'}
                            </td>
                            <td>
                                <span style={{ 
                                    color: getPriorityColor(ticket.priority),
                                    fontWeight: 'bold'
                                }}>
                                    {ticket.priority || 'Medium'}
                                </span>
                            </td>
                            <td>{ticket.order_number || '-'}</td>
                            <td>{ticket.reason}</td>
                            <td>{ticket.responsible}</td>
                            {showDays && (
                                <>
                                    <td>{calculateDays(ticket.created_at)} días</td>
                                    <td>
                                        {daysRemaining !== null ? (
                                            <span style={{ 
                                                color: daysRemaining < 0 ? '#dc3545' : daysRemaining <= 2 ? '#ffc107' : 'inherit',
                                                fontWeight: daysRemaining <= 2 ? 'bold' : 'normal'
                                            }}>
                                                {daysRemaining} días
                                            </span>
                                        ) : '-'}
                                    </td>
                                </>
                            )}
                            {showNotified && (
                                <td>
                                    {ticket.notified_at ? (
                                        <span style={{ color: '#28a745', fontWeight: 'bold' }}>
                                            ✓ {new Date(ticket.notified_at).toLocaleDateString()}
                                        </span>
                                    ) : (
                                        <span style={{ color: '#dc3545' }}>Pendiente</span>
                                    )}
                                </td>
                            )}
                            <td>{ticket.created_by_user?.username || '-'}</td>
                            <td>
                                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                    <button className="btn btn-sm btn-secondary" onClick={() => onSelect(ticket.id)}>
                                        Ver Detalle
                                    </button>
                                    {showNotified && !ticket.notified_at && onMarkNotified && (
                                        <button 
                                            className="btn btn-sm btn-primary" 
                                            onClick={() => onMarkNotified(ticket.id)}
                                            title="Marcar como notificado"
                                        >
                                            ✓ Notificar
                                        </button>
                                    )}
                                </div>
                            </td>
                        </tr>
                    )})}
                </tbody>
            </table>
        </div>
    );
}
